import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuthStore } from '@/store/useAuthStore';
import { ROUTES } from '@/utils/constants';
import { AuthProvider } from '@/components/AuthProvider';

// Pages
import HomePage from '@/pages/HomePage';
import PricingPage from '@/pages/PricingPage';
import DocsPage from '@/pages/DocsPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import DashboardLayout from '@/layouts/DashboardLayout';
import APIKeysPage from '@/pages/dashboard/APIKeysPage';
import UsagePage from '@/pages/dashboard/UsagePage';
import DashboardHome from '@/pages/dashboard/DashboardHome';
import APIDocsPage from '@/pages/dashboard/APIDocsPage';
import VoiceAssistantPage from '@/pages/dashboard/VoiceAssistantPage';

// Components
import ErrorFallback from '@/components/ErrorFallback';
import LoadingSpinner from '@/components/LoadingSpinner';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Public Routes */}
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.PRICING} element={<PricingPage />} />
            <Route path={ROUTES.DOCS} element={<DocsPage />} />
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
            
            {/* Protected Dashboard Routes */}
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="keys" element={<APIKeysPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="api-docs" element={<APIDocsPage />} />
              <Route path="voice-assistant" element={<VoiceAssistantPage />} />
            </Route>
            
            {/* 404 */}
            <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;

